import React, { useState, useCallback } from 'react';
import { Box, Chip, Tooltip, Popover, IconButton } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { MessageReaction } from '../../../types/chat';

interface MessageReactionsProps {
  reactions: MessageReaction[];
  currentUserId: string;
  onReactionPress: (emoji: string) => void;
  isCurrentUser: boolean;
  isHovered?: boolean;
}

interface EmojiPickerProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onEmojiSelect: (emoji: string) => void;
}

const COMMON_EMOJIS = [
  '❤️', '👍', '👎', '😂', '😢', '😮', '😡', '👏', 
  '🎉', '🔥', '💯', '✅', '❌', '⚡', '💪', '🙏'
];

const EmojiPicker: React.FC<EmojiPickerProps> = ({ anchorEl, open, onClose, onEmojiSelect }) => {
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      slotProps={{
        paper: {
          sx: {
            p: 1,
            maxWidth: '320px', // Increased from 280px
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
          }
        }
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(8, 1fr)',
          gap: 0.5,
        }}
      >
        {COMMON_EMOJIS.map((emoji) => (
          <IconButton
            key={emoji}
            onClick={() => onEmojiSelect(emoji)}
            sx={{
              fontSize: '20px',
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.15s ease',
            }}
          >
            {emoji}
          </IconButton>
        ))}
      </Box>
    </Popover>
  );
};

const MessageReactions: React.FC<MessageReactionsProps> = React.memo(({
  reactions,
  currentUserId,
  onReactionPress,
  isCurrentUser,
  isHovered = false
}) => {
  const [emojiPickerAnchor, setEmojiPickerAnchor] = useState<HTMLElement | null>(null);

  // Group reactions by emoji
  const groupedReactions = reactions.reduce((acc, reaction) => {
    if (!acc[reaction.emoji]) {
      acc[reaction.emoji] = [];
    }
    acc[reaction.emoji].push(reaction);
    return acc;
  }, {} as Record<string, MessageReaction[]>);

  const handleAddReaction = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setEmojiPickerAnchor(event.currentTarget);
  }, []);

  const handleEmojiSelect = useCallback((emoji: string) => {
    onReactionPress(emoji);
    setEmojiPickerAnchor(null);
  }, [onReactionPress]);

  const handleReactionClick = useCallback((emoji: string, event: React.MouseEvent) => {
    event.stopPropagation();
    onReactionPress(emoji);
  }, [onReactionPress]);

  if (reactions.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
          mt: 0.5,
          opacity: isHovered ? 1 : 0, // Show/hide based on hover state
          transition: 'opacity 0.2s ease',
          position: 'relative',
        }}
        className="reaction-hover-zone"
      >
        <IconButton
          size="small"
          onClick={handleAddReaction}
          sx={{
            width: '28px',
            height: '28px',
            backgroundColor: '#E3F2FD',
            border: '1px solid #1976D2',
            color: '#1976D2',
            '&:hover': {
              backgroundColor: '#BBDEFB',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.15s ease',
          }}
        >
          <AddIcon sx={{ fontSize: '16px', color: '#1976D2' }} />
        </IconButton>
        
        <EmojiPicker
          anchorEl={emojiPickerAnchor}
          open={Boolean(emojiPickerAnchor)}
          onClose={() => setEmojiPickerAnchor(null)}
          onEmojiSelect={handleEmojiSelect}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        mt: 0.5,
        alignItems: 'center',
        justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
      }}
    >
      {/* Add reaction button - show first for current user (right side) */}
      {isCurrentUser && (
        <IconButton
          size="small"
          onClick={handleAddReaction}
          sx={{
            width: '24px',
            height: '24px',
            backgroundColor: 'rgba(0, 0, 0, 0.06)',
            opacity: isHovered ? 1 : 0,
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              transform: 'scale(1.1)',
            },
          }}
        >
          <AddIcon 
            sx={{ 
              fontSize: '14px',
              color: '#1976D2'
            }} 
          />
        </IconButton>
      )}

      {Object.entries(groupedReactions).map(([emoji, reactionList]) => {
        const hasCurrentUserReaction = reactionList.some(r => r.uid === currentUserId);
        const tooltipText = reactionList.length === 1 
          ? `${reactionList[0].firstName} ${reactionList[0].lastName}`
          : reactionList.length === 2
          ? `${reactionList[0].firstName} and ${reactionList[1].firstName}`
          : `${reactionList[0].firstName} and ${reactionList.length - 1} others`;

        return (
          <Tooltip key={emoji} title={tooltipText} arrow>
            <Chip
              label={`${emoji} ${reactionList.length}`}
              size="small"
              clickable
              onClick={(event) => handleReactionClick(emoji, event)}
              sx={{
                height: '24px',
                fontSize: '12px',
                backgroundColor: hasCurrentUserReaction 
                  ? '#E3F2FD'
                  : 'rgba(0, 0, 0, 0.06)',
                color: hasCurrentUserReaction 
                  ? '#1976D2'
                  : '#374151',
                border: hasCurrentUserReaction 
                  ? '1px solid #1976D2'
                  : 'none',
                '&:hover': {
                  backgroundColor: hasCurrentUserReaction 
                    ? '#BBDEFB'
                    : 'rgba(0, 0, 0, 0.1)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.15s ease',
                '& .MuiChip-label': {
                  px: isCurrentUser ? 1 : 1, // Keep reduced padding for right side
                  fontWeight: hasCurrentUserReaction ? 600 : 400,
                },
              }}
            />
          </Tooltip>
        );
      })}
      
      {/* Add reaction button for non-current user (left side) */}
      {!isCurrentUser && (
        <IconButton
          size="small"
          onClick={handleAddReaction}
          sx={{
            width: '24px',
            height: '24px',
            backgroundColor: 'rgba(0, 0, 0, 0.06)',
            opacity: isHovered ? 1 : 0,
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              transform: 'scale(1.1)',
            },
          }}
        >
          <AddIcon 
            sx={{ 
              fontSize: '14px',
              color: '#6B7280'
            }} 
          />
        </IconButton>
      )}
      
      <EmojiPicker
        anchorEl={emojiPickerAnchor}
        open={Boolean(emojiPickerAnchor)}
        onClose={() => setEmojiPickerAnchor(null)}
        onEmojiSelect={handleEmojiSelect}
      />
    </Box>
  );
});

MessageReactions.displayName = 'MessageReactions';

export default MessageReactions;
